import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Avatar,
  IconButton,
  useDisclosure,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Grid,
  GridItem,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  useColorModeValue,
  Divider,
  SimpleGrid,
  Flex,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiEye,
  FiEdit,
  FiTrash,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiExternalLink,
  FiUsers,
  FiBriefcase,
  FiTrendingUp,
  FiSettings,
  FiPlay,
  FiStopCircle,
  FiLink,
  FiUnlink,
} from "react-icons/fi";
import sourcingService from "../../../services/sourcingService";

// Dynamic Platform Configuration
const PLATFORM_CONFIG = {
  LinkedIn: {
    icon: "💼",
    displayName: "LinkedIn",
    description: "Professional networking platform",
    requiredFields: ["apiKey", "clientId", "clientSecret"],
    optionalFields: ["redirectUri"],
    defaultCapabilities: {
      postJobs: true,
      fetchCandidates: true,
      searchCandidates: true,
      importProfiles: true,
    },
    apiDocs: "https://docs.microsoft.com/en-us/linkedin/",
    baseUrl: "https://api.linkedin.com",
  },
  Indeed: {
    icon: "🔍",
    displayName: "Indeed",
    description: "Job search engine",
    requiredFields: ["apiKey", "publisherId"],
    optionalFields: ["apiEndpoint"],
    defaultCapabilities: {
      postJobs: true,
      fetchCandidates: false,
      searchCandidates: true,
      importProfiles: false,
    },
    apiDocs: "https://developer.indeed.com/",
    baseUrl: "https://api.indeed.com",
  },
  Naukri: {
    icon: "🇮🇳",
    displayName: "Naukri",
    description: "Indian job portal",
    requiredFields: ["apiKey", "apiSecret"],
    optionalFields: ["redirectUri"],
    defaultCapabilities: {
      postJobs: true,
      fetchCandidates: true,
      searchCandidates: true,
      importProfiles: true,
    },
    apiDocs: "https://developer.naukri.com/",
    baseUrl: "https://api.naukri.com",
  },
  Monster: {
    icon: "👹",
    displayName: "Monster",
    description: "Global job board",
    requiredFields: ["apiKey", "accessToken"],
    optionalFields: ["clientId"],
    defaultCapabilities: {
      postJobs: true,
      fetchCandidates: false,
      searchCandidates: true,
      importProfiles: false,
    },
    apiDocs: "https://developer.monster.com/",
    baseUrl: "https://api.monster.com",
  },
  Glassdoor: {
    icon: "🚪",
    displayName: "Glassdoor",
    description: "Company reviews and jobs",
    requiredFields: ["apiKey", "partnerId"],
    optionalFields: ["apiSecret"],
    defaultCapabilities: {
      postJobs: false,
      fetchCandidates: false,
      searchCandidates: true,
      importProfiles: false,
    },
    apiDocs: "https://www.glassdoor.com/developer/index.htm",
    baseUrl: "https://api.glassdoor.com",
  },
  AngelList: {
    icon: "👼",
    displayName: "AngelList",
    description: "Startup jobs and talent",
    requiredFields: ["accessToken"],
    optionalFields: ["clientId", "clientSecret"],
    defaultCapabilities: {
      postJobs: true,
      fetchCandidates: true,
      searchCandidates: true,
      importProfiles: true,
    },
    apiDocs: "https://angel.co/api",
    baseUrl: "https://api.angel.co",
  },
  Dice: {
    icon: "🎲",
    displayName: "Dice",
    description: "Tech job board",
    requiredFields: ["apiKey", "clientId"],
    optionalFields: ["clientSecret", "redirectUri"],
    defaultCapabilities: {
      postJobs: true,
      fetchCandidates: false,
      searchCandidates: true,
      importProfiles: false,
    },
    apiDocs: "https://www.dice.com/external/developers",
    baseUrl: "https://api.dice.com",
  },
  Custom: {
    icon: "🔧",
    displayName: "Custom API",
    description: "Custom job portal integration",
    requiredFields: ["apiEndpoint", "apiKey"],
    optionalFields: [
      "apiSecret",
      "accessToken",
      "clientId",
      "clientSecret",
      "redirectUri",
    ],
    defaultCapabilities: {
      postJobs: true,
      fetchCandidates: true,
      searchCandidates: true,
      importProfiles: true,
    },
    apiDocs: null,
    baseUrl: null,
  },
};

// Helper functions for platform management
const getPlatformOptions = () => {
  return Object.keys(PLATFORM_CONFIG);
};

const getPlatformIcon = (platform) => {
  return PLATFORM_CONFIG[platform]?.icon || "🔗";
};

const getPlatformDisplayName = (platform) => {
  return PLATFORM_CONFIG[platform]?.displayName || platform;
};

const getPlatformDescription = (platform) => {
  return PLATFORM_CONFIG[platform]?.description || "";
};

const SourcingIntegrations = () => {
  const [integrations, setIntegrations] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stats, setStats] = useState({
    totalIntegrations: 0,
    activeIntegrations: 0,
    totalPostings: 0,
    totalCandidates: 0,
  });

  // Modal states
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isTestOpen,
    onOpen: onTestOpen,
    onClose: onTestClose,
  } = useDisclosure();
  const {
    isOpen: isPostJobOpen,
    onOpen: onPostJobOpen,
    onClose: onPostJobClose,
  } = useDisclosure();

  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [postingData, setPostingData] = useState({
    integrationId: "",
    requisitionId: "",
  });

  const toast = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [integrationsRes, postingsRes, candidatesRes] = await Promise.all([
        sourcingService.getAllIntegrations(),
        sourcingService.getJobPostings(),
        sourcingService.getCandidateProfiles({ limit: 100 }),
      ]);

      setIntegrations(integrationsRes.integrations || []);
      setJobPostings(postingsRes.postings || []);
      setCandidates(candidatesRes.candidates || []);

      // Calculate stats
      const totalIntegrations = integrationsRes.integrations?.length || 0;
      const activeIntegrations =
        integrationsRes.integrations?.filter((i) => i.isActive).length || 0;
      const totalPostings = postingsRes.postings?.length || 0;
      const totalCandidates = candidatesRes.candidates?.length || 0;

      setStats({
        totalIntegrations,
        activeIntegrations,
        totalPostings,
        totalCandidates,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error loading data",
        description: "Could not load sourcing integrations data",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch =
      integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.platform.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform =
      !platformFilter || integration.platform === platformFilter;
    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active"
        ? integration.isActive
        : !integration.isActive);

    return matchesSearch && matchesPlatform && matchesStatus;
  });

  const handleCreateIntegration = () => {
    setSelectedIntegration(null);
    onCreateOpen();
  };

  const handleViewIntegration = (integration) => {
    setSelectedIntegration(integration);
    onViewOpen();
  };

  const handleEditIntegration = (integration) => {
    setSelectedIntegration(integration);
    onEditOpen();
  };

  const handleDeleteIntegration = async (integration) => {
    if (
      window.confirm(
        `Are you sure you want to delete the ${integration.platform} integration?`
      )
    ) {
      try {
        await sourcingService.deleteIntegration(integration._id);
        toast({
          title: "Integration deleted",
          description: "Sourcing integration has been deleted successfully",
          status: "success",
          duration: 3000,
        });
        fetchData();
      } catch (error) {
        toast({
          title: "Error deleting integration",
          description: "Could not delete the integration",
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const handleTestIntegration = async (integration) => {
    setSelectedIntegration(integration);
    try {
      const result = await sourcingService.testIntegration(integration._id);
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || "Test failed",
      });
    }
    onTestOpen();
  };

  const handleToggleIntegration = async (integration) => {
    try {
      await sourcingService.updateIntegration(integration._id, {
        isActive: !integration.isActive,
      });
      toast({
        title: integration.isActive
          ? "Integration disabled"
          : "Integration enabled",
        description: `The ${integration.platform} integration has been ${
          integration.isActive ? "disabled" : "enabled"
        }`,
        status: "success",
        duration: 3000,
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error updating integration",
        description: "Could not update the integration status",
        status: "error",
        duration: 3000,
      });
    }
  };

  const getStatusBadgeColor = (status, isActive) => {
    if (!isActive) return "gray";
    switch (status) {
      case "success":
        return "green";
      case "error":
        return "red";
      case "syncing":
        return "yellow";
      default:
        return "blue";
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="xl" color="blue.500" mb={4} />
        <Text>Loading sourcing integrations...</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              Sourcing Integrations
            </Text>
            <Text color="gray.600">
              Connect with job portals and manage external candidate sourcing
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleCreateIntegration}
          >
            Add Integration
          </Button>
        </HStack>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Integrations</StatLabel>
                <StatNumber>{stats.totalIntegrations}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Active Integrations</StatLabel>
                <StatNumber>{stats.activeIntegrations}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Job Postings</StatLabel>
                <StatNumber>{stats.totalPostings}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Candidate Profiles</StatLabel>
                <StatNumber>{stats.totalCandidates}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Filters */}
        <Card>
          <CardBody>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
              gap={4}
            >
              <Box>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search integrations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Box>
              <Box>
                <Select
                  placeholder="All Platforms"
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                >
                  {getPlatformOptions().map((platform) => (
                    <option key={platform} value={platform}>
                      {platform}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Select
                  placeholder="All Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </Box>
              <Box>
                <Button
                  leftIcon={<FiFilter />}
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setPlatformFilter("");
                    setStatusFilter("");
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </CardBody>
        </Card>

        {/* Integrations Table */}
        <Card>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Platform</Th>
                  <Th>Name</Th>
                  <Th>Status</Th>
                  <Th>Last Sync</Th>
                  <Th>Capabilities</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredIntegrations.map((integration) => (
                  <Tr key={integration._id}>
                    <Td>
                      <HStack>
                        <Text fontSize="lg">
                          {getPlatformIcon(integration.platform)}
                        </Text>
                        <Box>
                          <Text fontWeight="medium">
                            {getPlatformDisplayName(integration.platform)}
                          </Text>
                        </Box>
                      </HStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{integration.name}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {integration.description}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <HStack>
                        <Badge
                          colorScheme={integration.isActive ? "green" : "gray"}
                          variant="subtle"
                        >
                          {integration.isActive ? "Active" : "Inactive"}
                        </Badge>
                        {integration.syncStatus && (
                          <Badge
                            colorScheme={getStatusBadgeColor(
                              integration.syncStatus,
                              integration.isActive
                            )}
                            variant="subtle"
                          >
                            {integration.syncStatus}
                          </Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td>
                      {integration.lastSyncDate ? (
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm">
                            {new Date(
                              integration.lastSyncDate
                            ).toLocaleDateString()}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {new Date(
                              integration.lastSyncDate
                            ).toLocaleTimeString()}
                          </Text>
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          Never
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        {integration.capabilities?.postJobs && (
                          <Tooltip label="Can post jobs">
                            <Icon as={FiBriefcase} color="blue.500" />
                          </Tooltip>
                        )}
                        {integration.capabilities?.fetchCandidates && (
                          <Tooltip label="Can fetch candidates">
                            <Icon as={FiUsers} color="green.500" />
                          </Tooltip>
                        )}
                        {integration.capabilities?.searchCandidates && (
                          <Tooltip label="Can search candidates">
                            <Icon as={FiSearch} color="purple.500" />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="View Details">
                          <IconButton
                            icon={<FiEye />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewIntegration(integration)}
                          />
                        </Tooltip>
                        <Tooltip label="Edit">
                          <IconButton
                            icon={<FiEdit />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditIntegration(integration)}
                          />
                        </Tooltip>
                        <Tooltip label="Test Connection">
                          <IconButton
                            icon={<FiRefreshCw />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleTestIntegration(integration)}
                          />
                        </Tooltip>
                        <Tooltip
                          label={integration.isActive ? "Disable" : "Enable"}
                        >
                          <IconButton
                            icon={
                              integration.isActive ? (
                                <FiStopCircle />
                              ) : (
                                <FiPlay />
                              )
                            }
                            size="sm"
                            variant="ghost"
                            colorScheme={integration.isActive ? "red" : "green"}
                            onClick={() => handleToggleIntegration(integration)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete">
                          <IconButton
                            icon={<FiTrash />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteIntegration(integration)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {filteredIntegrations.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">
                  {integrations.length === 0
                    ? "No sourcing integrations found. Add your first integration to get started."
                    : "No integrations match your filters"}
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Create/Edit Integration Modal */}
      <Modal
        isOpen={isCreateOpen || isEditOpen}
        onClose={isCreateOpen ? onCreateClose : onEditClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isCreateOpen ? "Add New Integration" : "Edit Integration"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <IntegrationForm
              integration={selectedIntegration}
              onSuccess={() => {
                fetchData();
                isCreateOpen ? onCreateClose() : onEditClose();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* View Integration Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Integration Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedIntegration && (
              <VStack spacing={6} align="stretch">
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={4}
                >
                  <Box>
                    <Text fontWeight="bold">Platform</Text>
                    <HStack mt={1}>
                      <Text fontSize="lg">
                        {getPlatformIcon(selectedIntegration.platform)}
                      </Text>
                      <Text>
                        {getPlatformDisplayName(selectedIntegration.platform)}
                      </Text>
                    </HStack>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Status</Text>
                    <Badge
                      colorScheme={
                        selectedIntegration.isActive ? "green" : "gray"
                      }
                      variant="subtle"
                      mt={1}
                    >
                      {selectedIntegration.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Name</Text>
                    <Text mt={1}>{selectedIntegration.name}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Description</Text>
                    <Text mt={1}>
                      {selectedIntegration.description || "No description"}
                    </Text>
                  </Box>
                </Grid>

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb={3}>
                    Capabilities
                  </Text>
                  <SimpleGrid columns={2} spacing={2}>
                    <HStack>
                      <Icon
                        as={FiBriefcase}
                        color={
                          selectedIntegration.capabilities?.postJobs
                            ? "green.500"
                            : "gray.400"
                        }
                      />
                      <Text>Post Jobs</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiUsers}
                        color={
                          selectedIntegration.capabilities?.fetchCandidates
                            ? "green.500"
                            : "gray.400"
                        }
                      />
                      <Text>Fetch Candidates</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiSearch}
                        color={
                          selectedIntegration.capabilities?.searchCandidates
                            ? "green.500"
                            : "gray.400"
                        }
                      />
                      <Text>Search Candidates</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiLink}
                        color={
                          selectedIntegration.capabilities?.importProfiles
                            ? "green.500"
                            : "gray.400"
                        }
                      />
                      <Text>Import Profiles</Text>
                    </HStack>
                  </SimpleGrid>
                </Box>

                {selectedIntegration.lastSyncDate && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={2}>
                        Last Sync Information
                      </Text>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                        gap={4}
                      >
                        <Box>
                          <Text fontSize="sm" color="gray.600">
                            Last Sync Date
                          </Text>
                          <Text>
                            {new Date(
                              selectedIntegration.lastSyncDate
                            ).toLocaleString()}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.600">
                            Sync Status
                          </Text>
                          <Badge
                            colorScheme={getStatusBadgeColor(
                              selectedIntegration.syncStatus,
                              selectedIntegration.isActive
                            )}
                            variant="subtle"
                          >
                            {selectedIntegration.syncStatus}
                          </Badge>
                        </Box>
                      </Grid>
                    </Box>
                  </>
                )}

                {selectedIntegration.lastError && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={2} color="red.500">
                        Last Error
                      </Text>
                      <Alert status="error">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>Error Details</AlertTitle>
                          <AlertDescription>
                            {selectedIntegration.lastError.message}
                            <br />
                            <Text fontSize="sm" mt={1}>
                              {new Date(
                                selectedIntegration.lastError.timestamp
                              ).toLocaleString()}
                            </Text>
                          </AlertDescription>
                        </Box>
                      </Alert>
                    </Box>
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Test Connection Modal */}
      <Modal isOpen={isTestOpen} onClose={onTestClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Test Integration Connection</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedIntegration && testResult && (
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Text fontSize="lg">
                    {getPlatformIcon(selectedIntegration.platform)}
                  </Text>
                  <Text fontWeight="bold">{selectedIntegration.name}</Text>
                </HStack>

                <Alert status={testResult.success ? "success" : "error"}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle>
                      {testResult.success
                        ? "Connection Successful!"
                        : "Connection Failed"}
                    </AlertTitle>
                    <AlertDescription>{testResult.message}</AlertDescription>
                  </Box>
                </Alert>

                {testResult.details && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Response Details:
                    </Text>
                    <Box
                      bg="gray.50"
                      p={3}
                      borderRadius="md"
                      fontSize="sm"
                      fontFamily="mono"
                      maxH="200px"
                      overflowY="auto"
                    >
                      <pre>{JSON.stringify(testResult.details, null, 2)}</pre>
                    </Box>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onTestClose}>
              Close
            </Button>
            {selectedIntegration && (
              <Button
                colorScheme="blue"
                onClick={() => handleTestIntegration(selectedIntegration)}
                ml={3}
              >
                Test Again
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Integration Form Component
const IntegrationForm = ({ integration, onSuccess }) => {
  const [formData, setFormData] = useState({
    platform: "",
    name: "",
    description: "",
    apiEndpoint: "",
    apiKey: "",
    apiSecret: "",
    accessToken: "",
    clientId: "",
    clientSecret: "",
    redirectUri: "",
    isActive: true,
    autoSync: false,
    syncFrequency: "manual",
    capabilities: {
      postJobs: false,
      fetchCandidates: false,
      searchCandidates: false,
      importProfiles: false,
    },
  });

  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (integration) {
      setFormData({
        platform: integration.platform || "",
        name: integration.name || "",
        description: integration.description || "",
        apiEndpoint: integration.apiEndpoint || "",
        apiKey: integration.apiKey || "",
        apiSecret: integration.apiSecret || "",
        accessToken: integration.accessToken || "",
        clientId: integration.clientId || "",
        clientSecret: integration.clientSecret || "",
        redirectUri: integration.redirectUri || "",
        isActive: integration.isActive ?? true,
        autoSync: integration.autoSync ?? false,
        syncFrequency: integration.syncFrequency || "manual",
        capabilities: integration.capabilities || {
          postJobs: false,
          fetchCandidates: false,
          searchCandidates: false,
          importProfiles: false,
        },
      });
    } else {
      setFormData({
        platform: "",
        name: "",
        description: "",
        apiEndpoint: "",
        apiKey: "",
        apiSecret: "",
        accessToken: "",
        clientId: "",
        clientSecret: "",
        redirectUri: "",
        isActive: true,
        autoSync: false,
        syncFrequency: "manual",
        capabilities: {
          postJobs: false,
          fetchCandidates: false,
          searchCandidates: false,
          importProfiles: false,
        },
      });
    }
  }, [integration]);

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleCapabilityChange = (capability, value) => {
    setFormData((prev) => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [capability]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.platform || !formData.name) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      if (integration) {
        await sourcingService.updateIntegration(integration._id, formData);
        toast({
          title: "Integration updated",
          description: "Sourcing integration has been updated successfully",
          status: "success",
          duration: 3000,
        });
      } else {
        await sourcingService.createIntegration(formData);
        toast({
          title: "Integration created",
          description: "Sourcing integration has been created successfully",
          status: "success",
          duration: 3000,
        });
      }

      onSuccess();
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error saving integration",
        description: "Could not save the integration",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const getRequiredFieldsForPlatform = (platform) => {
    return PLATFORM_CONFIG[platform]?.requiredFields || [];
  };

  const getOptionalFieldsForPlatform = (platform) => {
    return PLATFORM_CONFIG[platform]?.optionalFields || [];
  };

  const getDefaultCapabilitiesForPlatform = (platform) => {
    return (
      PLATFORM_CONFIG[platform]?.defaultCapabilities || {
        postJobs: false,
        fetchCandidates: false,
        searchCandidates: false,
        importProfiles: false,
      }
    );
  };

  const requiredFields = getRequiredFieldsForPlatform(formData.platform);
  const optionalFields = getOptionalFieldsForPlatform(formData.platform);

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={6} align="stretch">
        {/* Basic Information */}
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Basic Information
          </Text>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
            <FormControl isRequired>
              <FormLabel>Platform</FormLabel>
              <Select
                value={formData.platform}
                onChange={(e) => {
                  const selectedPlatform = e.target.value;
                  handleInputChange("platform", selectedPlatform);
                  // Set default capabilities for the selected platform
                  if (selectedPlatform && !integration) {
                    const defaultCapabilities =
                      getDefaultCapabilitiesForPlatform(selectedPlatform);
                    setFormData((prev) => ({
                      ...prev,
                      platform: selectedPlatform,
                      capabilities: { ...defaultCapabilities },
                    }));
                  }
                }}
                disabled={!!integration} // Can't change platform once created
              >
                <option value="">Select Platform</option>
                {getPlatformOptions().map((platform) => (
                  <option key={platform} value={platform}>
                    {getPlatformIcon(platform)}{" "}
                    {getPlatformDisplayName(platform)}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Integration Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., LinkedIn Premium Account"
              />
            </FormControl>

            <FormControl gridColumn={{ md: "span 2" }}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder={
                  formData.platform
                    ? getPlatformDescription(formData.platform)
                    : "Describe this integration..."
                }
                rows={2}
              />
            </FormControl>
          </Grid>
        </Box>

        {/* API Configuration */}
        {formData.platform && (
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              API Configuration
            </Text>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={4}
            >
              {/* Required Fields */}
              {requiredFields.map((field) => (
                <FormControl key={field} isRequired>
                  <FormLabel>
                    {field.charAt(0).toUpperCase() +
                      field.slice(1).replace(/([A-Z])/g, " $1")}
                  </FormLabel>
                  <Input
                    type={
                      field.includes("Secret") ||
                      field.includes("Token") ||
                      field.includes("Key")
                        ? "password"
                        : "text"
                    }
                    value={formData[field] || ""}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    placeholder={`Enter ${
                      field.charAt(0).toUpperCase() +
                      field.slice(1).replace(/([A-Z])/g, " $1")
                    }`}
                  />
                </FormControl>
              ))}

              {/* Optional Fields */}
              {optionalFields.map((field) => (
                <FormControl key={field}>
                  <FormLabel>
                    {field.charAt(0).toUpperCase() +
                      field.slice(1).replace(/([A-Z])/g, " $1")}{" "}
                    (Optional)
                  </FormLabel>
                  <Input
                    type={
                      field.includes("Secret") ||
                      field.includes("Token") ||
                      field.includes("Key")
                        ? "password"
                        : "text"
                    }
                    value={formData[field] || ""}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    placeholder={`Enter ${
                      field.charAt(0).toUpperCase() +
                      field.slice(1).replace(/([A-Z])/g, " $1")
                    }`}
                  />
                </FormControl>
              ))}
            </Grid>
          </Box>
        )}

        {/* Capabilities */}
        {formData.platform && (
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Capabilities
            </Text>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Default capabilities for{" "}
              {getPlatformDisplayName(formData.platform)} are pre-selected. You
              can customize them as needed.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {Object.entries({
                postJobs: "Post Jobs",
                fetchCandidates: "Fetch Candidates",
                searchCandidates: "Search Candidates",
                importProfiles: "Import Profiles",
              }).map(([key, label]) => (
                <FormControl key={key}>
                  <HStack>
                    <input
                      type="checkbox"
                      checked={formData.capabilities[key] || false}
                      onChange={(e) =>
                        handleCapabilityChange(key, e.target.checked)
                      }
                    />
                    <FormLabel mb={0}>{label}</FormLabel>
                  </HStack>
                </FormControl>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Settings */}
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Settings
          </Text>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
            <FormControl>
              <HStack>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleInputChange("isActive", e.target.checked)
                  }
                />
                <FormLabel mb={0}>Active</FormLabel>
              </HStack>
            </FormControl>

            <FormControl>
              <HStack>
                <input
                  type="checkbox"
                  checked={formData.autoSync}
                  onChange={(e) =>
                    handleInputChange("autoSync", e.target.checked)
                  }
                />
                <FormLabel mb={0}>Auto Sync</FormLabel>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>Sync Frequency</FormLabel>
              <Select
                value={formData.syncFrequency}
                onChange={(e) =>
                  handleInputChange("syncFrequency", e.target.value)
                }
                disabled={!formData.autoSync}
              >
                <option value="manual">Manual</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </Select>
            </FormControl>
          </Grid>
        </Box>
      </VStack>

      <ModalFooter px={0} pb={0} pt={6}>
        <Button variant="ghost" type="button" onClick={() => {}}>
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          type="submit"
          isLoading={loading}
          loadingText={integration ? "Updating..." : "Creating..."}
          ml={3}
        >
          {integration ? "Update Integration" : "Create Integration"}
        </Button>
      </ModalFooter>
    </Box>
  );
};

export default SourcingIntegrations;

// Helper functions for dynamic platform management
export const addPlatform = (platformKey, config) => {
  PLATFORM_CONFIG[platformKey] = config;
};

export const getPlatformConfig = (platform) => {
  return PLATFORM_CONFIG[platform];
};

export const getAllPlatforms = () => {
  return Object.keys(PLATFORM_CONFIG);
};
